import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import { captureRef } from './services/creatorApi';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import SkipLink from './components/SkipLink';
import PageViewTracker from './components/PageViewTracker';
import TrailingSlashRedirect from './components/TrailingSlashRedirect';
import AdSenseLoader from './components/AdSenseLoader';
import NewsletterWelcomeModal from './components/NewsletterWelcomeModal';
import './App.css';
import EnhancedArticlePage from './pages/EnhancedArticlePage';

// Lazy load pages for code splitting
const HomePage = lazy(() => import('./pages/HomePage'));
const NewsStoryPage = lazy(() => import('./pages/NewsStoryPage'));
const NewsHubPage = lazy(() => import('./pages/NewsHubPage'));
const AppsPage = lazy(() => import('./pages/AppsPage'));
const MediaKitBuilderPage = lazy(() => import('./pages/MediaKitBuilderPage'));
const PublicMediaKitPage = lazy(() => import('./pages/PublicMediaKitPage'));
const MediaKitsPage = lazy(() => import('./pages/dashboard/MediaKitsPage'));
const CreatorProfilePage = lazy(() => import('./pages/dashboard/CreatorProfilePage'));
const EventsPage = lazy(() => import('./pages/EventsPage'));
const TopicPage = lazy(() => import('./pages/TopicPage'));
const LeaderboardPage = lazy(() => import('./pages/LeaderboardPage'));
const SavesPage = lazy(() => import('./pages/SavesPage'));
// EnhancedArticlePage is NOT lazy-loaded to prevent CLS from Suspense fallback
// import is at top of file
const CategoriesPage = lazy(() => import('./pages/CategoriesPage'));
const CategoryPage = lazy(() => import('./pages/CategoryPage'));
const TagPage = lazy(() => import('./pages/TagPage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const AuthorPage = lazy(() => import('./pages/AuthorPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const StaticPage = lazy(() => import('./pages/StaticPage'));
const ComparisonPage = lazy(() => import('./pages/ComparisonPage'));
const ProductDetailsPage = lazy(() => import('./pages/ProductDetailsPage'));
const ProductCategoryPage = lazy(() => import('./pages/ProductCategoryPage'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const GSTCalculatorPage = lazy(() => import('./pages/GSTCalculatorPage'));
const InvoiceGeneratorPage = lazy(() => import('./pages/InvoiceGeneratorPage'));
const InvoiceBuilderPage = lazy(() => import('./pages/InvoiceBuilderPage'));
const SharedInvoicePage = lazy(() => import('./pages/SharedInvoicePage'));
const BusinessToolsPage = lazy(() => import('./pages/BusinessToolsPage'));
const RentReceiptPage = lazy(() => import('./pages/RentReceiptPage'));
const TdsCalculatorPage = lazy(() => import('./pages/TdsCalculatorPage'));
const SalarySlipPage = lazy(() => import('./pages/SalarySlipPage'));
const MarginCalculatorPage = lazy(() => import('./pages/MarginCalculatorPage'));
const EmiCalculatorPage = lazy(() => import('./pages/EmiCalculatorPage'));
const BreakEvenCalculatorPage = lazy(() => import('./pages/BreakEvenCalculatorPage'));
const FreelanceRateCalculatorPage = lazy(() => import('./pages/FreelanceRateCalculatorPage'));
const ExpenseTrackerPage = lazy(() => import('./pages/ExpenseTrackerPage'));
const TimesheetPage = lazy(() => import('./pages/TimesheetPage'));
const LetterheadPage = lazy(() => import('./pages/LetterheadPage'));
const PasswordGeneratorPage = lazy(() => import('./pages/PasswordGeneratorPage'));
const PerfectWeightCalculatorPage = lazy(() => import('./pages/PerfectWeightCalculatorPage'));
const CalorieCalculatorPage = lazy(() => import('./pages/CalorieCalculatorPage'));
const QRCodeGeneratorPage = lazy(() => import('./pages/QRCodeGeneratorPage'));
const ReverseImageSearchPage = lazy(() => import('./pages/ReverseImageSearchPage'));
const PlagiarismCheckerPage = lazy(() => import('./pages/PlagiarismCheckerPage'));
const GrammarCheckerPage = lazy(() => import('./pages/GrammarCheckerPage'));
const AIWritingAssistantPage = lazy(() => import('./pages/AIWritingAssistantPage'));
const PDFEditorPage = lazy(() => import('./pages/PDFEditorPage'));
const BackgroundRemoverPage = lazy(() => import('./pages/BackgroundRemoverPage'));
const ImageCompressorPage = lazy(() => import('./pages/ImageCompressorPage'));
const OnPageSEOCheckerPage = lazy(() => import('./pages/OnPageSEOCheckerPage'));
const GoogleRankCheckerPage = lazy(() => import('./pages/GoogleRankCheckerPage'));
const WhatIsMyIPPage = lazy(() => import('./pages/WhatIsMyIPPage'));
const SpeedTestPage = lazy(() => import('./pages/SpeedTestPage'));
const ToolsPage = lazy(() => import('./pages/ToolsPage'));
const AIToolsPage = lazy(() => import('./pages/AIToolsPage'));
const AIToolDetailPage = lazy(() => import('./pages/AIToolDetailPage'));
const AIToolCategoryPage = lazy(() => import('./pages/AIToolCategoryPage'));
const SubmitToolPage = lazy(() => import('./pages/SubmitToolPage'));
const AIMentionsPage = lazy(() => import('./pages/AIMentionsPage'));
const NewsletterConfirmPage = lazy(() => import('./pages/NewsletterConfirmPage'));
const NewsletterUnsubscribePage = lazy(() => import('./pages/NewsletterUnsubscribePage'));
const NewsletterPreferencesPage = lazy(() => import('./pages/NewsletterPreferencesPage'));
const NewsletterPublicViewPage = lazy(() => import('./pages/NewsletterPublicViewPage'));
const NewsletterWelcomePage = lazy(() => import('./pages/NewsletterWelcomePage'));
const NewsletterPage = lazy(() => import('./pages/NewsletterPage'));
const SocialMediaGraphicsPage = lazy(() => import('./pages/SocialMediaGraphicsPage'));
const CronExpressionGeneratorPage = lazy(() => import('./pages/CronExpressionGeneratorPage'));
const PublishArticlePage = lazy(() => import('./pages/PublishArticlePage'));
const AdvertisePage = lazy(() => import('./pages/AdvertisePage'));
const EditorialStandardsPage = lazy(() => import('./pages/EditorialStandardsPage'));
const LogoMakerPage = lazy(() => import('./pages/LogoMakerPage'));
const MotionControlPage = lazy(() => import('./pages/MotionControlPage'));
const ImageLibraryPage = lazy(() => import('./pages/ImageLibraryPage'));
const VideoLibraryPage = lazy(() => import('./pages/VideoLibraryPage'));
const VideoCategoryPage = lazy(() => import('./pages/VideoCategoryPage'));
const VideoDetailPage = lazy(() => import('./pages/VideoDetailPage'));
const ImageCategoryPage = lazy(() => import('./pages/ImageCategoryPage'));
const ImageDetailPage = lazy(() => import('./pages/ImageDetailPage'));
const ImageFavoritesPage = lazy(() => import('./pages/ImageFavoritesPage'));
const ImageCollectionPage = lazy(() => import('./pages/ImageCollectionPage'));
const PhotographerPage = lazy(() => import('./pages/PhotographerPage'));

// Guides Module
const GuidesHomePage = lazy(() => import('./pages/guides/GuidesHomePage'));
const GuideDetailPage = lazy(() => import('./pages/guides/GuideDetailPage'));

// Social Media Module
const SocialHomePage = lazy(() => import('./pages/social/SocialHomePage'));
const SocialProfilePage = lazy(() => import('./pages/social/ProfilePage'));
const SocialPostPage = lazy(() => import('./pages/social/PostPage'));
const SocialCreatePage = lazy(() => import('./components/social/PostComposer'));
const SocialNotificationsPage = lazy(() => import('./pages/social/NotificationsPage'));
const SocialSavedPage = lazy(() => import('./pages/social/SavedPage'));
const SocialExplorePage = lazy(() => import('./pages/social/ExplorePage'));
const EarnLandingPage = lazy(() => import('./pages/EarnLandingPage'));

// Auth & Settings Pages
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const SettingsPage = lazy(() => import('./pages/dashboard/SettingsPage'));
const DashboardLayout = lazy(() => import('./pages/dashboard/DashboardLayout'));
const DashboardOverviewPage = lazy(() => import('./pages/dashboard/DashboardOverviewPage'));
const DashboardEarningsPage = lazy(() => import('./pages/dashboard/DashboardEarningsPage'));
const MyContentPage = lazy(() => import('./pages/dashboard/MyContentPage'));
const BalancePage = lazy(() => import('./pages/dashboard/BalancePage'));
const ToolsActivityPage = lazy(() => import('./pages/dashboard/ToolsActivityPage'));
const InvoicesPage = lazy(() => import('./pages/dashboard/InvoicesPage'));
const ClientsPage = lazy(() => import('./pages/dashboard/ClientsPage'));
const InvoiceSettingsPage = lazy(() => import('./pages/dashboard/InvoiceSettingsPage'));

// Loading component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

function AppContent() {
  const location = useLocation();
  const isGuidesPage = location.pathname.startsWith('/guides');

  useEffect(() => {
    captureRef();
  }, []);

  return (
    <>
      <SkipLink />
      <ScrollToTop />
      <PageViewTracker />
      <TrailingSlashRedirect />
      <AdSenseLoader />
      <NewsletterWelcomeModal />
      <div className="min-h-screen bg-white">
        {!isGuidesPage && <Navigation />}

        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/news" element={<NewsHubPage />} />
            <Route path="/news/" element={<NewsHubPage />} />
            <Route path="/apps" element={<AppsPage />} />
            <Route path="/apps/" element={<AppsPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/events/" element={<EventsPage />} />
            <Route path="/news/topic/:slug" element={<TopicPage />} />
            <Route path="/news/topic/:slug/" element={<TopicPage />} />
            <Route path="/news/:slug" element={<NewsStoryPage />} />
            <Route path="/news/:slug/" element={<NewsStoryPage />} />
            <Route path="/leaderboard" element={<LeaderboardPage />} />
            <Route path="/leaderboard/" element={<LeaderboardPage />} />
            <Route path="/saves" element={<SavesPage />} />
            <Route path="/article/:slug" element={<EnhancedArticlePage />} />
            <Route path="/categories" element={<CategoriesPage />} />
            <Route path="/category/:slug" element={<CategoryPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/tag/:slug" element={<TagPage />} />
            <Route path="/author/:username" element={<AuthorPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/compare" element={<ComparisonPage />} />
            <Route path="/compare/:slug" element={<ComparisonPage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/products/" element={<ProductsPage />} />
            <Route path="/products/category/:slug" element={<ProductCategoryPage />} />
            <Route path="/product/:slug" element={<ProductDetailsPage />} />
            {/* Tool Pages - Both with and without trailing slash for redirect */}
            <Route path="/free-gst-calculator" element={<GSTCalculatorPage />} />
            <Route path="/free-gst-calculator/" element={<GSTCalculatorPage />} />
            <Route path="/free-invoice-generator" element={<InvoiceBuilderPage key="invoice" />} />
            <Route path="/free-invoice-generator/" element={<InvoiceBuilderPage key="invoice" />} />
            <Route path="/proforma-invoice-generator" element={<InvoiceBuilderPage key="proforma" documentType="proforma" />} />
            <Route path="/proforma-invoice-generator/" element={<InvoiceBuilderPage key="proforma" documentType="proforma" />} />
            <Route path="/free-letterhead-maker" element={<LetterheadPage />} />
            <Route path="/free-letterhead-maker/" element={<LetterheadPage />} />
            <Route path="/free-timesheet" element={<TimesheetPage />} />
            <Route path="/free-timesheet/" element={<TimesheetPage />} />
            <Route path="/free-expense-tracker" element={<ExpenseTrackerPage />} />
            <Route path="/free-expense-tracker/" element={<ExpenseTrackerPage />} />
            <Route path="/free-margin-calculator" element={<MarginCalculatorPage />} />
            <Route path="/free-margin-calculator/" element={<MarginCalculatorPage />} />
            <Route path="/free-emi-calculator" element={<EmiCalculatorPage />} />
            <Route path="/free-emi-calculator/" element={<EmiCalculatorPage />} />
            <Route path="/free-break-even-calculator" element={<BreakEvenCalculatorPage />} />
            <Route path="/free-break-even-calculator/" element={<BreakEvenCalculatorPage />} />
            <Route path="/free-freelance-rate-calculator" element={<FreelanceRateCalculatorPage />} />
            <Route path="/free-freelance-rate-calculator/" element={<FreelanceRateCalculatorPage />} />
            <Route path="/free-salary-slip-generator" element={<SalarySlipPage />} />
            <Route path="/free-salary-slip-generator/" element={<SalarySlipPage />} />
            <Route path="/free-tds-calculator" element={<TdsCalculatorPage />} />
            <Route path="/free-tds-calculator/" element={<TdsCalculatorPage />} />
            <Route path="/free-rent-receipt-generator" element={<RentReceiptPage />} />
            <Route path="/free-rent-receipt-generator/" element={<RentReceiptPage />} />
            <Route path="/free-business-tools" element={<BusinessToolsPage />} />
            <Route path="/free-business-tools/" element={<BusinessToolsPage />} />
            <Route path="/free-credit-note-generator" element={<InvoiceBuilderPage key="credit" documentType="credit" />} />
            <Route path="/free-credit-note-generator/" element={<InvoiceBuilderPage key="credit" documentType="credit" />} />
            <Route path="/free-purchase-order-generator" element={<InvoiceBuilderPage key="po" documentType="po" />} />
            <Route path="/free-purchase-order-generator/" element={<InvoiceBuilderPage key="po" documentType="po" />} />
            <Route path="/free-receipt-generator" element={<InvoiceBuilderPage key="receipt" documentType="receipt" />} />
            <Route path="/free-receipt-generator/" element={<InvoiceBuilderPage key="receipt" documentType="receipt" />} />
            <Route path="/free-estimate-generator" element={<InvoiceBuilderPage key="quote" documentType="quote" />} />
            <Route path="/free-estimate-generator/" element={<InvoiceBuilderPage key="quote" documentType="quote" />} />
            <Route path="/free-media-kit-generator" element={<MediaKitBuilderPage />} />
            <Route path="/free-media-kit-generator/" element={<MediaKitBuilderPage />} />
            <Route path="/k/:slug" element={<PublicMediaKitPage />} />
            {/* Old wizard kept reachable for one release as a rollback path. */}
            <Route path="/free-invoice-generator/classic" element={<InvoiceGeneratorPage />} />
            <Route path="/invoice/:shareToken" element={<SharedInvoicePage />} />
            <Route path="/password-generator" element={<PasswordGeneratorPage />} />
            <Route path="/password-generator/" element={<PasswordGeneratorPage />} />
            <Route path="/perfect-weight-calculator" element={<PerfectWeightCalculatorPage />} />
            <Route path="/perfect-weight-calculator/" element={<PerfectWeightCalculatorPage />} />
            <Route path="/calorie-calculator" element={<CalorieCalculatorPage />} />
            <Route path="/calorie-calculator/" element={<CalorieCalculatorPage />} />
            <Route path="/qr-code-generator" element={<QRCodeGeneratorPage />} />
            <Route path="/qr-code-generator/" element={<QRCodeGeneratorPage />} />
            <Route path="/reverse-image-search" element={<ReverseImageSearchPage />} />
            <Route path="/reverse-image-search/" element={<ReverseImageSearchPage />} />
            <Route path="/plagiarism-checker" element={<PlagiarismCheckerPage />} />
            <Route path="/plagiarism-checker/" element={<PlagiarismCheckerPage />} />
            <Route path="/free-grammar-checker" element={<GrammarCheckerPage />} />
            <Route path="/free-grammar-checker/" element={<GrammarCheckerPage />} />
            <Route path="/free-ai-writing-assistant" element={<AIWritingAssistantPage />} />
            <Route path="/free-ai-writing-assistant/" element={<AIWritingAssistantPage />} />
            <Route path="/free-online-pdf-editor" element={<PDFEditorPage />} />
            <Route path="/free-online-pdf-editor/" element={<PDFEditorPage />} />
            <Route path="/free-background-remover-for-images" element={<BackgroundRemoverPage />} />
            <Route path="/free-background-remover-for-images/" element={<BackgroundRemoverPage />} />
            <Route path="/free-image-compressor" element={<ImageCompressorPage />} />
            <Route path="/free-image-compressor/" element={<ImageCompressorPage />} />
            <Route path="/on-page-seo-checker" element={<OnPageSEOCheckerPage />} />
            <Route path="/on-page-seo-checker/" element={<OnPageSEOCheckerPage />} />
            <Route path="/google-rank-checker" element={<GoogleRankCheckerPage />} />
            <Route path="/google-rank-checker/" element={<GoogleRankCheckerPage />} />
            <Route path="/what-is-my-ip" element={<WhatIsMyIPPage />} />
            <Route path="/what-is-my-ip/" element={<WhatIsMyIPPage />} />
            <Route path="/speed-test" element={<SpeedTestPage />} />
            <Route path="/speed-test/" element={<SpeedTestPage />} />
            <Route path="/tools" element={<ToolsPage />} />
            <Route path="/tools/" element={<ToolsPage />} />
            <Route path="/ai-tools" element={<AIToolsPage />} />
            <Route path="/ai-tools/" element={<AIToolsPage />} />
            <Route path="/ai-tools/submit" element={<SubmitToolPage />} />
            <Route path="/ai-tools/submit/" element={<SubmitToolPage />} />
            <Route path="/ai-tools/category/:slug" element={<AIToolCategoryPage />} />
            <Route path="/ai-tools/category/:slug/" element={<AIToolCategoryPage />} />
            <Route path="/ai-tools/:slug" element={<AIToolDetailPage />} />
            <Route path="/ai-tools/:slug/" element={<AIToolDetailPage />} />
            <Route path="/ai-mentions" element={<AIMentionsPage />} />
            <Route path="/ai-mentions/" element={<AIMentionsPage />} />
            <Route path="/social-media-graphics-generator" element={<SocialMediaGraphicsPage />} />
            <Route path="/social-media-graphics-generator/" element={<SocialMediaGraphicsPage />} />
            <Route path="/cron-expression-generator" element={<CronExpressionGeneratorPage />} />
            <Route path="/cron-expression-generator/" element={<CronExpressionGeneratorPage />} />

            {/* Publish Article */}
            <Route path="/publish-article" element={<PublishArticlePage />} />
            <Route path="/publish-article/" element={<PublishArticlePage />} />

            {/* Advertise / Sponsor Posts */}
            <Route path="/advertise" element={<AdvertisePage />} />
            <Route path="/advertise/" element={<AdvertisePage />} />

            {/* Editorial Standards / Masthead */}
            <Route path="/editorial-standards" element={<EditorialStandardsPage />} />
            <Route path="/editorial-standards/" element={<EditorialStandardsPage />} />

            {/* Logo Maker - AI Logo Generator */}
            <Route path="/logo-maker-ai" element={<LogoMakerPage />} />
            <Route path="/logo-maker-ai/" element={<LogoMakerPage />} />
            <Route path="/ai/motion-control" element={<MotionControlPage />} />
            <Route path="/ai/motion-control/" element={<MotionControlPage />} />

            {/* Social Media Module */}
            <Route path="/social" element={<SocialHomePage />} />
            <Route path="/social/" element={<SocialHomePage />} />
            <Route path="/earn" element={<EarnLandingPage />} />
            <Route path="/earn/" element={<EarnLandingPage />} />
            <Route path="/social/explore" element={<SocialExplorePage />} />
            <Route path="/social/create" element={<SocialCreatePage />} />
            <Route path="/social/profile" element={<SocialProfilePage />} />
            <Route path="/social/earnings" element={<Navigate to="/dashboard/earnings" replace />} />
            <Route path="/social/:username" element={<SocialProfilePage />} />
            <Route path="/social/notifications" element={<SocialNotificationsPage />} />
            <Route path="/social/bookmarks" element={<SocialSavedPage />} />
            <Route path="/social/saved" element={<SocialSavedPage />} />
            <Route path="/social/tag/:tag" element={<SocialHomePage />} />
            <Route path="/social/post/:slug" element={<SocialPostPage />} />

            {/* Auth Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />

            {/* Dashboard Routes */}
            <Route path="/dashboard" element={<DashboardLayout />}>
              <Route index element={<DashboardOverviewPage />} />
              <Route path="earnings" element={<DashboardEarningsPage />} />
              <Route path="content" element={<MyContentPage />} />
              <Route path="balance" element={<BalancePage />} />
              <Route path="tools" element={<ToolsActivityPage />} />
              <Route path="invoices" element={<InvoicesPage />} />
              <Route path="clients" element={<ClientsPage />} />
              <Route path="invoice-settings" element={<InvoiceSettingsPage />} />
              <Route path="media-kits" element={<MediaKitsPage />} />
              <Route path="creator-profile" element={<CreatorProfilePage />} />
              <Route path="settings" element={<SettingsPage />} />
            </Route>

            {/* Newsletter landing */}
            <Route path="/newsletter" element={<NewsletterPage />} />
            <Route path="/newsletter/" element={<NewsletterPage />} />

            {/* Newsletter Confirmation */}
            <Route path="/newsletter/confirm/:token" element={<NewsletterConfirmPage />} />
            <Route path="/newsletter/unsubscribe/:token" element={<NewsletterUnsubscribePage />} />
            {/* 3 segments, so it cannot collide with /newsletter/:id below. */}
            <Route path="/newsletter/preferences/:token" element={<NewsletterPreferencesPage />} />
            <Route path="/newsletter/welcome" element={<NewsletterWelcomePage />} />
            <Route path="/newsletter/welcome/" element={<NewsletterWelcomePage />} />
            <Route path="/newsletter/:id" element={<NewsletterPublicViewPage />} />

            {/* Image Library */}
            <Route path="/images/favorites" element={<ImageFavoritesPage />} />
            <Route path="/images/favorites/" element={<ImageFavoritesPage />} />
            <Route path="/images/collections/:slug" element={<ImageCollectionPage />} />
            <Route path="/images/collections/:slug/" element={<ImageCollectionPage />} />
            <Route path="/images/photographer/:name" element={<PhotographerPage />} />
            <Route path="/images/photographer/:name/" element={<PhotographerPage />} />
            <Route path="/images" element={<ImageLibraryPage />} />
            <Route path="/images/" element={<ImageLibraryPage />} />
            <Route path="/images/:categorySlug/:imageSlug" element={<ImageDetailPage />} />
            <Route path="/images/:categorySlug/:imageSlug/" element={<ImageDetailPage />} />
            <Route path="/images/:slug" element={<ImageCategoryPage />} />
            <Route path="/images/:slug/" element={<ImageCategoryPage />} />

            {/* Guides Module — own layout, no blog nav/footer */}
            <Route path="/guides" element={<GuidesHomePage />} />
            <Route path="/guides/" element={<GuidesHomePage />} />
            <Route path="/guides/:slug" element={<GuideDetailPage />} />
            <Route path="/guides/:slug/" element={<GuideDetailPage />} />

            {/* Video Library Routes */}
            <Route path="/videos" element={<VideoLibraryPage />} />
            <Route path="/videos/" element={<VideoLibraryPage />} />
            <Route path="/videos/:categorySlug/:videoSlug" element={<VideoDetailPage />} />
            <Route path="/videos/:categorySlug/:videoSlug/" element={<VideoDetailPage />} />
            <Route path="/videos/:slug" element={<VideoCategoryPage />} />
            <Route path="/videos/:slug/" element={<VideoCategoryPage />} />

            {/* Legacy photo URL redirects */}
            <Route path="/photos/riya-sen-wallpapers/*" element={<Navigate to="/article/riya-sen-wallpapers/" replace />} />

            <Route path="/privacy" element={<StaticPage />} />
            <Route path="/terms" element={<StaticPage />} />
            <Route path="/:pageType" element={<StaticPage />} />
            <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>

            {!isGuidesPage && <Footer />}
          </div>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
    </HelmetProvider>
  );
}


const NotFoundPage = () => (
  <div className="container mx-auto px-4 py-16 text-center">
    <h1 className="text-4xl font-bold text-gray-900 mb-4">404 - Page Not Found</h1>
    <p className="text-gray-600 mb-8">The page you're looking for doesn't exist.</p>
    <Link to="/" className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
      Back to Home
    </Link>
  </div>
);

export default App;